import { DescriptionOutlined } from '@mui/icons-material';
import { Alert, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { ExtractionUsageCounter } from '../../../components/extraction-usage-counter';
import { getLocalToday } from '../../../config/timezone';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import { useNotification } from '../../../hooks/use-notification';
import type { DeliveryNoteDuplicateSummary } from '../../../services/goods-receiving/goods-receiving.types';
import { formatDateForInput, parseDate } from '../../../utils/date-utils';
import { DuplicateDeliveryDialog } from '../components/duplicate-delivery-dialog';
import { PdfPagesNotReadyAlert } from '../components/pdf-pages-not-ready-alert';
import { ExistingPhotosGrid } from '../components/photo-upload/existing-photos-grid';
import { PhotoUpload, type PhotoUploadHandle } from '../components/photo-upload/photo-upload';
import {
  classifyUploadFailure,
  getHttpStatusFromError,
  isPdfPagesNotReady,
  shouldReportUploadError,
} from '../components/upload-failure-message';
import { WizardNavigation } from '../components/wizard-navigation';
import { WizardStepHeader } from '../components/wizard-step-header';
import { useAddPhotos, useCheckDuplicate, usePatchDraft } from '../hooks/use-draft-mutations';
import { getStepRoute, useWizardDraft } from '../hooks/use-wizard-draft';
import { goodsReceivingQueryKeys } from '../query-keys';
import { isAppendPagesRequested } from '../utils/ddt-pages-notice';
import { WizardLayout } from './wizard-layout';

function canUploadPhotos(
  photos: File[],
  draftId: string | null,
  isUploading: boolean,
): draftId is string {
  return photos.length > 0 && !!draftId && !isUploading;
}

export function DocumentStepPage() {
  const { t } = useTranslation('goodsReceiving');
  const isMobile = useIsMobile();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');
  const appendPages = isAppendPagesRequested(searchParams);

  const queryClient = useQueryClient();
  const { data: draft, refetch: refetchDraft } = useWizardDraft(draftId);
  const patchDraft = usePatchDraft();
  const checkDuplicate = useCheckDuplicate();
  const addPhotos = useAddPhotos();

  const [documentNumber, setDocumentNumber] = useState<string>(draft?.documentNumber ?? '');
  const [documentDate, setDocumentDate] = useState<Date | null>(
    draft?.date ? parseDate(draft.date) : getLocalToday(),
  );
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]);
  const [isPhotoProcessing, setIsPhotoProcessing] = useState(false);
  const [pdfPagesNotReady, setPdfPagesNotReady] = useState(false);
  const [duplicates, setDuplicates] = useState<DeliveryNoteDuplicateSummary[]>([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  // Track whether the user has locally modified the fields (typing).
  // When true, draft refetches must not override the local value.
  const locallyModifiedRef = useRef(false);
  const photoUploadRef = useRef<PhotoUploadHandle>(null);

  const isExtracting = draft?.extractionStatus === 'PENDING';
  const isUploading = addPhotos.isPending;

  // Sync when draft loads/updates (for resume and after extraction completes).
  // Skip if the user has already modified the fields via typing.
  useEffect(() => {
    if (locallyModifiedRef.current) {
      return;
    }
    if (draft?.documentNumber) {
      setDocumentNumber(draft.documentNumber);
    }
    if (draft?.date) {
      setDocumentDate(parseDate(draft.date));
    }
  }, [draft?.documentNumber, draft?.date]);

  const handleDocumentNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    locallyModifiedRef.current = true;
    setDocumentNumber(e.target.value);
  };

  const handlePhotoChange = async (photos: File[]) => {
    setPendingPhotos(photos);
    if (!canUploadPhotos(photos, draftId, isUploading)) {
      return;
    }
    setPdfPagesNotReady(false);
    try {
      const result = await addPhotos.mutateAsync({
        draftId,
        files: photos,
        options: { extract: true, appendPages },
      });
      photoUploadRef.current?.syncBackendPhotos(result);
      setPendingPhotos([]);
    } catch (error) {
      await handlePhotoUploadError(error);
    }
  };

  const handlePhotoUploadError = async (error: unknown) => {
    const httpStatus = getHttpStatusFromError(error);

    // The picked photos stay in `pendingPhotos`: the retry button resends them as they are.
    if (isPdfPagesNotReady(httpStatus)) {
      setPdfPagesNotReady(true);
      return;
    }

    // If the receipt was completed from another tab/device while this page
    // was open, refresh its status. useWizardDraft owns the single warning
    // and redirects to history, avoiding a stack of generic error toasts.
    if (httpStatus === 400) {
      const refreshed = await refetchDraft();
      if (refreshed.data && refreshed.data.status !== 'DRAFT') {
        return;
      }
    }

    const isNetworkFailure = classifyUploadFailure(httpStatus) === 'network';
    if (shouldReportUploadError(error)) {
      Sentry.captureException(error, {
        tags: { wizard_step: 'document' },
      });
    }
    showError(isNetworkFailure ? t('photoExtractionNetworkError') : t('stepSaveError'));
  };

  const goToItemsStep = () => {
    if (draftId) {
      navigate(`${getStepRoute('items')}?draftId=${draftId}`, { flushSync: true });
    }
  };

  const handleNext = async () => {
    if (!draftId) {
      return;
    }
    patchDraft.mutate(
      {
        id: draftId,
        data: {
          documentNumber: documentNumber.trim() || undefined,
          date: formatDateForInput(documentDate) || undefined,
          currentStep: 'items',
        },
      },
      {
        onSuccess: () => {
          // Duplicate check must resolve before navigation — the dialog lives
          // inside this step component and is unmounted by goToItemsStep().
          checkDuplicate.mutate(draftId, {
            onSuccess: (result) => {
              if (result.duplicates.length > 0) {
                setDuplicates(result.duplicates);
                setDuplicateDialogOpen(true);
              } else {
                goToItemsStep();
              }
            },
            onError: () => {
              // If duplicate check fails, don't block — proceed to items step.
              goToItemsStep();
            },
          });
        },
      },
    );
  };

  const handleProceedAnyway = () => {
    setDuplicateDialogOpen(false);
    goToItemsStep();
  };

  return (
    <WizardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <WizardStepHeader
          step="document"
          icon={<DescriptionOutlined />}
          title={t('documentTitle')}
          subtitle={t('documentSubtitle')}
        />

        {draft?.photos && draft.photos.length > 0 && draftId && (
          <ExistingPhotosGrid
            photos={draft.photos}
            deliveryNoteId={draftId}
            onPhotoDeleted={() => {
              queryClient.invalidateQueries({
                queryKey: goodsReceivingQueryKeys.draft(draftId),
              });
            }}
          />
        )}

        <PhotoUpload
          ref={photoUploadRef}
          value={pendingPhotos}
          onChange={handlePhotoChange}
          onProcessingChange={setIsPhotoProcessing}
          maxFiles={10}
          label={t('photoDdt')}
          acceptPdf
          hideEmptyState={!!draft?.photos?.length}
          onPreviewReady={() => {
            if (draftId) {
              queryClient.invalidateQueries({ queryKey: goodsReceivingQueryKeys.draft(draftId) });
            }
          }}
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          {t('common:uploadHints.aiDisclaimer')}
        </Alert>

        <ExtractionUsageCounter />

        {(isExtracting || isUploading) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('extractingData')}
            </Typography>
          </Box>
        )}

        {pdfPagesNotReady && (
          <PdfPagesNotReadyAlert
            isRetrying={isUploading}
            onRetry={() => {
              void handlePhotoChange(pendingPhotos);
            }}
          />
        )}

        {draft?.extractionStatus === 'FAILED' && (
          <Alert severity="warning">{t('extractionFailed')}</Alert>
        )}

        {!draft?.photos?.length && !isUploading && (
          <Alert severity="info">{t('photoOptional')}</Alert>
        )}

        <TextField
          label={t('documentNumber')}
          value={documentNumber}
          onChange={handleDocumentNumberChange}
          fullWidth
          required
          slotProps={{ input: { style: { fontSize: isMobile ? '16px' : undefined } } }}
          error={!documentNumber.trim()}
          helperText={!documentNumber.trim() ? t('documentNumberRequired') : undefined}
        />

        <DatePicker
          label={t('documentDate')}
          value={documentDate}
          onChange={(date) => setDocumentDate(date)}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error: !documentDate,
              helperText: !documentDate ? t('documentDateRequired') : undefined,
              slotProps: { input: { style: { fontSize: isMobile ? '16px' : undefined } } },
            },
          }}
        />
      </Box>
      <WizardNavigation
        canGoBack={true}
        canGoNext={!!documentNumber.trim() && !!documentDate}
        onBack={() =>
          navigate(`${getStepRoute('supplier')}?draftId=${draftId}`, { flushSync: true })
        }
        onNext={handleNext}
        nextLabel={t('next')}
        isLoading={
          isExtracting ||
          isPhotoProcessing ||
          isUploading ||
          patchDraft.isPending ||
          checkDuplicate.isPending
        }
      />
      <DuplicateDeliveryDialog
        open={duplicateDialogOpen}
        duplicates={duplicates}
        onClose={() => setDuplicateDialogOpen(false)}
        onProceed={handleProceedAnyway}
      />
    </WizardLayout>
  );
}
